import { useState, useEffect } from 'react';
import { History, RotateCcw, Clock, Hash, Loader2 } from 'lucide-react';
import ConfirmDialog from '../common/ConfirmDialog';
import edgeService from '../../services/edge.service';
import { useToast } from '../common/ToastContainer';
import { useTenantStore } from '../../stores/tenantStore';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';

interface ConfigSnapshot {
  id: number;
  sequenceNumber: number;
  configHash: string | null;
  reason: string | null;
  createdAt: string;
  validatedAt: string | null;
}

interface ConfigHistoryProps {
  edgeId: string;
}

export default function ConfigHistory({ edgeId }: ConfigHistoryProps) {
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const dateSettings = useSettingsStore().getDateFormatSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [snapshots, setSnapshots] = useState<ConfigSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState<ConfigSnapshot | null>(null);
  const [restoring, setRestoring] = useState(false);

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const data = await edgeService.getConfigHistory(edgeId);
      setSnapshots(data.snapshots || []);
    } catch (err: any) {
      toast.error('Load Failed', `Failed to load config history: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSnapshots();
    }
  }, [isOpen, edgeId, selectedTenantId]);

  const handleRestore = async () => {
    if (!restoreConfirm) return;
    setRestoring(true);
    try {
      await edgeService.restoreConfigSnapshot(edgeId, restoreConfirm.id);
      setRestoreConfirm(null);
      setIsOpen(false);
      toast.success('Config Restored', 'Configuration restored from snapshot. Sync the edge to apply changes.');
    } catch (err: any) {
      toast.error('Restore Failed', err.message);
    } finally {
      setRestoring(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <History className="w-4 h-4" />
        <span>Config History</span>
      </button>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center space-x-2">
            <History className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-700">Config History</h3>
            <span className="text-xs text-gray-400">(Last 5 valid configs)</span>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Close
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
              <span className="text-sm text-gray-500">Loading history...</span>
            </div>
          ) : snapshots.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No config snapshots available yet. Snapshots are saved when configs are synced and confirmed by the edge.
            </p>
          ) : (
            <div className="space-y-2">
              {snapshots.map((snapshot) => (
                <div
                  key={snapshot.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 text-sm">
                      <span className="inline-flex items-center space-x-1 text-gray-600">
                        <Hash className="w-3.5 h-3.5" />
                        <span className="font-mono">{snapshot.sequenceNumber}</span>
                      </span>
                      <span className="inline-flex items-center space-x-1 text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatDate(snapshot.validatedAt || snapshot.createdAt, dateSettings)}</span>
                      </span>
                    </div>
                    {snapshot.reason && (
                      <p className="text-xs text-gray-400 mt-1 truncate">{snapshot.reason}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setRestoreConfirm(snapshot)}
                    className="ml-3 inline-flex items-center space-x-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Restore</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!restoreConfirm}
        onClose={() => setRestoreConfirm(null)}
        onConfirm={handleRestore}
        title="Restore Configuration"
        message={`Restore config from sequence #${restoreConfirm?.sequenceNumber || ''}? Current configurations will be replaced with this snapshot. You will need to sync the edge to apply changes.`}
        confirmText="Restore"
        variant="warning"
        isLoading={restoring}
      />
    </>
  );
}
